import classNames from 'classnames';
import equal from 'fast-deep-equal';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';

type Props = {
  currentPage: number;
  totalAmount: number;
  onPageChange: (p: number) => void;
  loading: boolean;
  perPage: number;

  v2?: boolean;
};

export const UiPagination: React.FC<Props> = memo(
  ({ currentPage, perPage, totalAmount, onPageChange, loading, v2 = false }: Props) => {
    const { t } = useTranslation('', { keyPrefix: 'common.pagination' });

    const onPageChangeLocal = useCallback(
      ({ selected }: { selected: number }) => {
        onPageChange(selected);
      },
      [onPageChange]
    );

    const [totalPages, setTotalPages] = useState(0); // calculate total amount of pages

    useEffect(() => {
      // if amount is empty (a case of loading) - do not change total amount of pages
      if (!loading) {
        setTotalPages(Math.ceil(totalAmount / perPage));
      }
    }, [loading, perPage, totalAmount]);

    if (totalPages > 1) {
      if (v2) {
        return (
          <ReactPaginate
            previousLabel={
              <>
                <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    /* eslint-disable-next-line max-len */
                    d="M0.000626998 5.9682C0.00487423 5.85001 0.0335569 5.73398 0.0848355 5.6274C0.136114 5.52083 0.208913 5.42602 0.298618 5.34895L6.30025 0.205867C6.38586 0.132654 6.48501 0.0770214 6.59212 0.0421452C6.69922 0.00726899 6.81211 -0.00616704 6.9244 0.00260426C7.0367 0.0113756 7.14622 0.0421823 7.24661 0.0932654C7.347 0.144349 7.43632 0.214707 7.50952 0.300324C7.58271 0.38594 7.63833 0.485137 7.67319 0.592249C7.70805 0.699361 7.72151 0.812291 7.71272 0.924589C7.70393 1.03689 7.67309 1.14635 7.62199 1.24674C7.57089 1.34712 7.50046 1.43645 7.41483 1.50964L2.16969 6L7.41483 10.4904C7.50046 10.5635 7.57089 10.6529 7.62199 10.7533C7.67309 10.8536 7.70393 10.9631 7.71272 11.0754C7.72151 11.1877 7.70805 11.3006 7.67319 11.4078C7.63833 11.5149 7.58271 11.6141 7.50952 11.6997C7.43632 11.7853 7.347 11.8557 7.24661 11.9067C7.14622 11.9578 7.0367 11.9886 6.9244 11.9974C6.81211 12.0062 6.69922 11.9927 6.59212 11.9579C6.48501 11.923 6.38586 11.8673 6.30025 11.7941L0.298618 6.65105C0.200763 6.56699 0.123084 6.46193 0.0714926 6.3437C0.0199016 6.22546 -0.00430424 6.09711 0.000626998 5.9682V5.9682Z"
                    fill="#828282"
                  />
                </svg>
                {t('prev')}
              </>
            }
            nextLabel={
              <>
                {t('next')}
                <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    /* eslint-disable-next-line max-len */
                    d="M7.99937 5.9682C7.99513 5.85001 7.96644 5.73398 7.91516 5.6274C7.86389 5.52083 7.79109 5.42602 7.70138 5.34895L1.69975 0.205867C1.61414 0.132654 1.51499 0.0770214 1.40788 0.0421452C1.30078 0.00726899 1.18789 -0.00616704 1.0756 0.00260426C0.963297 0.0113756 0.853781 0.0421823 0.753389 0.0932654C0.652997 0.144349 0.563682 0.214707 0.490485 0.300324C0.417287 0.38594 0.361669 0.485137 0.326812 0.592249C0.291955 0.699361 0.278486 0.812291 0.287277 0.924589C0.296069 1.03689 0.326909 1.14635 0.378009 1.24674C0.42911 1.34712 0.499541 1.43645 0.58517 1.50964L5.83031 6L0.58517 10.4904C0.499541 10.5635 0.42911 10.6529 0.378009 10.7533C0.326909 10.8536 0.296069 10.9631 0.287277 11.0754C0.278486 11.1877 0.291955 11.3006 0.326812 11.4078C0.361669 11.5149 0.417287 11.6141 0.490485 11.6997C0.563682 11.7853 0.652997 11.8557 0.753389 11.9067C0.853781 11.9578 0.963297 11.9886 1.0756 11.9974C1.18789 12.0062 1.30078 11.9927 1.40788 11.9579C1.51499 11.923 1.61414 11.8673 1.69975 11.7941L7.70138 6.65105C7.79924 6.56699 7.87692 6.46193 7.92851 6.3437C7.9801 6.22546 8.0043 6.09711 7.99937 5.9682Z"
                    fill="#272756"
                  />
                </svg>
              </>
            }
            forcePage={currentPage}
            pageCount={totalPages}
            marginPagesDisplayed={1}
            pageRangeDisplayed={totalPages <= 7 ? 7 : 3}
            onPageChange={onPageChangeLocal}
            previousClassName="prev"
            containerClassName={classNames('pagination pagination-v2', {
              disabled: loading,
            })}
            activeClassName="active"
          />
        );
      }
      return (
        <ReactPaginate
          previousLabel="«"
          nextLabel="»"
          forcePage={currentPage}
          pageCount={totalPages}
          marginPagesDisplayed={1}
          pageRangeDisplayed={totalPages <= 7 ? 7 : 3}
          onPageChange={onPageChangeLocal}
          containerClassName={classNames('pagination pull-right', {
            disabled: loading,
          })}
          activeClassName="active"
        />
      );
    }
    return null;
  },
  equal
);
